import * as fontkitNs from '@pdf-lib/fontkit';
const fontkit = fontkitNs.default ?? fontkitNs;
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { formatInr } from './formatInr';

/**
 * `public/invoice-templates/invoice-sample-3.pdf` — A4; we only fill:
 * - Client block under “Bill To”
 * - White mask on the right clears a stray **template horizontal rule** between date and table
 * - **Current date** under “INVOICE” (right column)
 * - Main bordered table + **separate** narrow “Invoice Amount” mini-table (right-aligned, inset from edge).
 */
const INVOICE_TEMPLATE = '/invoice-templates/invoice-sample-3.pdf';

/** Table chrome (aligned with template lines ~75–520, header text ~265). */
const S3 = {
  tableLeft: 72,
  tableRight: 520,
  /** Clear template table/header ghosts, redraw our table. */
  tableMaskTop: 256,
  /** White only through here: redrawn table + mini-total + small buffer (keeps template payment/footer). */
  tableClearBuffer: 14,
  billMask: { left: 70, top: 174, right: 430, bottom: 253 },
  billTextX: 72,
  billTextStartY: 178,
  /** Narrower column so long addresses wrap sooner (less “spread” across the page). */
  billMaxWidth: 300,
  billLineHeight: 11.5,
  /** Under “INVOICE” (title ends ~122 from top). */
  dateRight: 528,
  dateBaselineFromTop: 132,
  /** Template-only rule between date and table (x > bill text). Cleared before we redraw. */
  templateRuleClear: { left: 424, top: 136, right: 530, bottom: 256 },
  /** Header band (below top rule ~233). */
  headerTop: 257,
  headerBottom: 281,
  /** First body row text baseline (inside first body band). */
  row0Baseline: 299,
  /** Taller body rows (reference invoice). */
  rowStep: 28,
  maxRows: 10,
  /** ~10% / ~70% / ~20% of inner width (tableRight - tableLeft). */
  colSr: 82,
  colDesc: 126,
  colSep1: 118,
  colSep2: 432,
  descMaxWidth: 296,
  colAmountRight: 508,
  /** Muted mauve header (reference). */
  headerFill: rgb(92 / 255, 76 / 255, 108 / 255),
  headerText: rgb(1, 1, 1),
  bodyText: rgb(0.12, 0.12, 0.12),
  lineColor: rgb(0.86, 0.87, 0.89),
  /** Thin black table rules (reference). */
  tableBorder: rgb(0.02, 0.02, 0.02),
  tableLineThickness: 0.85,
  /** Mini total row height (separate from main table). */
  totalRowHeight: 26,
  /** White gap between last body row and mini total table. */
  totalGapBelowMain: 12,
  /** Mini table width; right edge aligns with main table minus `tableInnerRightMargin`. */
  totalMiniWidth: 228,
  /** Vertical split inside mini table: label cell | amount cell. */
  totalMiniLabelSplit: 138,
  /** Padding inside mini-table amount cell (keeps figures off the border line). */
  tableInnerRightMargin: 6,
  white: rgb(1, 1, 1),
};

function publicAsset(path) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const rel = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${rel}` : rel;
}

function looksLikeSupportedFont(bytes) {
  if (!bytes || bytes.byteLength < 4) return false;
  const u8 = new Uint8Array(bytes, 0, 4);
  const tag = String.fromCharCode(u8[0], u8[1], u8[2], u8[3]);
  return tag === 'OTTO' || tag === 'true' || tag === 'ttcf' || (u8[0] === 0x00 && u8[1] === 0x01 && u8[2] === 0x00 && u8[3] === 0x00);
}

function pdfSafeText(value) {
  return String(value ?? '')
    .replace(/\u20b9/g, 'Rs.')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '');
}

async function fetchFontBytes(path) {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('text/html') || contentType.includes('text/plain')) return null;
    const bytes = await res.arrayBuffer();
    return looksLikeSupportedFont(bytes) ? bytes : null;
  } catch {
    return null;
  }
}

async function embedFontOrFallback(pdfDoc, fontBytes, fallbackFont) {
  if (!fontBytes) return fallbackFont;
  try {
    return await pdfDoc.embedFont(fontBytes, { subset: true });
  } catch {
    return fallbackFont;
  }
}

function inrCell(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  return formatInr(n);
}

function lineAmount(it) {
  if (it.amount != null && Number.isFinite(Number(it.amount))) return Number(it.amount);
  const base = (it.qty || 1) * (it.rate || 0);
  const disc = base * (it.discount || 0) / 100;
  const gstA = (base - disc) * (it.gst || 0) / 100;
  return Math.round((base - disc + gstA) * 100) / 100;
}

function titleCaseWords(s) {
  return String(s || '')
    .split(/(\s+)/)
    .map((part) => {
      if (!part.trim()) return part;
      if (part.length <= 2) return part.toLowerCase();
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('');
}

/**
 * Bill To as separate lines (name / street or property / city+state+pin / landmark)
 * so the PDF is not one long comma-separated run.
 */
export function formatClientBillLines(client, project) {
  const c = client && typeof client === 'object' ? client : {};
  const p = project && typeof project === 'object' ? project : {};
  const lines = [];

  const name = String(c.name || c.company || '').trim() || '—';
  lines.push(titleCaseWords(name));

  const prop = String(c.propertyAddress || p.propertyAddress || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (prop) {
    lines.push(titleCaseWords(prop));
  } else {
    const a = c.address;
    if (a && typeof a === 'object') {
      const street = [a.line1 || a.street, a.line2].filter(Boolean).join(', ').trim();
      if (street) lines.push(titleCaseWords(street));
    }
  }

  const city = String(c.address?.city || p.city || '').trim();
  const state = String(c.address?.state || p.state || '').trim();
  const pin = String(c.address?.pincode || '').trim();
  const loc = [];
  if (city) loc.push(city);
  if (state && state.toLowerCase() !== city.toLowerCase()) loc.push(state);
  const cityState = loc.join(', ');
  let locLine = '';
  if (cityState && pin) locLine = `${cityState} - ${pin}`;
  else if (cityState) locLine = cityState;
  else if (pin) locLine = pin;
  if (locLine) lines.push(titleCaseWords(locLine));

  const landmark = String(c.address?.location || '').trim();
  if (landmark) lines.push(titleCaseWords(landmark));

  return lines.length ? lines : ['—'];
}

/** Single-line comma form (legacy); prefer {@link formatClientBillLines} for layout. */
export function formatClientBillCommaLine(client, project) {
  return formatClientBillLines(client, project).join(', ');
}

function wrapToWidth(font, text, size, maxW) {
  const words = pdfSafeText(text).split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines = [];
  let line = '';
  const pushLine = (l) => {
    if (l) lines.push(l);
  };
  for (const w of words) {
    const trial = line ? `${line} ${w}` : w;
    if (font.widthOfTextAtSize(trial, size) <= maxW) line = trial;
    else {
      if (line) pushLine(line);
      if (font.widthOfTextAtSize(w, size) <= maxW) line = w;
      else {
        let chunk = '';
        for (const ch of w) {
          const t = chunk + ch;
          if (font.widthOfTextAtSize(t, size) <= maxW) chunk = t;
          else {
            if (chunk) pushLine(chunk);
            chunk = ch;
          }
        }
        line = chunk;
      }
    }
  }
  pushLine(line);
  return lines.length ? lines : [''];
}

function fillRectFromTop(page, H, left, top, right, bottom, color) {
  page.drawRectangle({
    x: left,
    y: H - bottom,
    width: right - left,
    height: bottom - top,
    color,
    borderWidth: 0,
  });
}

function baselineY(H, yFromTop) {
  return H - yFromTop;
}

/** yFromTop increases downward; PDF y increases upward. */
function drawHLineTop(page, H, left, right, yFromTop, thickness, color) {
  const y = H - yFromTop;
  page.drawLine({
    start: { x: left, y },
    end: { x: right, y },
    thickness,
    color,
  });
}

function drawVLineTop(page, H, x, topFromTop, bottomFromTop, thickness, color) {
  page.drawLine({
    start: { x, y: H - topFromTop },
    end: { x, y: H - bottomFromTop },
    thickness,
    color,
  });
}

/** Main item table only (ends at body bottom; total is drawn separately). */
function drawInvoiceTableGrid(page, H, T, bodyGridRows, bodyBottom) {
  const border = T.tableBorder;
  const t = T.tableLineThickness;
  const left = T.tableLeft;
  const right = T.tableRight;
  const top = T.headerTop;

  drawVLineTop(page, H, left, top, bodyBottom, t, border);
  drawVLineTop(page, H, right, top, bodyBottom, t, border);
  drawHLineTop(page, H, left, right, top, t, border);
  drawHLineTop(page, H, left, right, bodyBottom, t, border);
  drawVLineTop(page, H, T.colSep1, top, bodyBottom, t, border);
  drawVLineTop(page, H, T.colSep2, top, bodyBottom, t, border);

  drawHLineTop(page, H, left, right, T.headerBottom, t, border);
  for (let k = 1; k <= bodyGridRows; k++) {
    drawHLineTop(page, H, left, right, T.headerBottom + k * T.rowStep, t, border);
  }
}

/** Narrow right-aligned “Invoice Amount | total” box. */
function drawTotalMiniTableGrid(page, H, T, miniLeft, miniRight, sepX, rowTop, rowBottom) {
  const border = T.tableBorder;
  const t = T.tableLineThickness;
  drawHLineTop(page, H, miniLeft, miniRight, rowTop, t, border);
  drawHLineTop(page, H, miniLeft, miniRight, rowBottom, t, border);
  drawVLineTop(page, H, miniLeft, rowTop, rowBottom, t, border);
  drawVLineTop(page, H, miniRight, rowTop, rowBottom, t, border);
  drawVLineTop(page, H, sepX, rowTop, rowBottom, t, border);
}

/**
 * @param {object} invoice
 * @param {object|null} client
 * @param {object|null} project
 */
export async function generateInvoicePDFBlob(invoice, client, project = null) {
  const templatePath = publicAsset(INVOICE_TEMPLATE);
  const fontPath = publicAsset('/fonts/Montserrat-Regular.ttf');

  const [templateRes, fontBytes] = await Promise.all([
    fetch(templatePath),
    fetchFontBytes(fontPath),
  ]);
  if (!templateRes.ok) throw new Error(`Invoice template not found (${templateRes.status}).`);

  const templateBytes = await templateRes.arrayBuffer();

  const pdfDoc = await PDFDocument.load(templateBytes);
  pdfDoc.registerFontkit(fontkit);
  const fallbackFont = await pdfDoc.embedStandardFont(StandardFonts.Helvetica);
  const fallbackFontBold = await pdfDoc.embedStandardFont(StandardFonts.HelveticaBold);
  const font = await embedFontOrFallback(pdfDoc, fontBytes, fallbackFont);
  const fontBold = fontBytes ? font : fallbackFontBold;
  const page = pdfDoc.getPages()[0];
  const H = page.getHeight();
  const T = S3;

  const bodySize = 10.5;
  const headerLabelSize = 10.5;
  const tableSize = 10.5;
  const dateSize = 10.5;

  const items = (invoice.items || []).slice(0, T.maxRows);
  const bodyGridRows = Math.max(items.length, 1);
  const bodyBottom = T.headerBottom + bodyGridRows * T.rowStep;
  const totalRowTop = bodyBottom + T.totalGapBelowMain;
  const totalRowBottom = totalRowTop + T.totalRowHeight;
  const totalMiniRight = T.tableRight;
  const totalMiniLeft = totalMiniRight - T.totalMiniWidth;
  const totalMiniSep = totalMiniLeft + T.totalMiniLabelSplit;
  const tableClearBottom = totalRowBottom + T.tableClearBuffer;

  // ── Clear areas we redraw (table mask must NOT extend into template payment/footer) ──
  fillRectFromTop(page, H, T.billMask.left, T.billMask.top, T.billMask.right, T.billMask.bottom, T.white);
  fillRectFromTop(page, H, T.tableLeft, T.tableMaskTop, T.tableRight, tableClearBottom, T.white);
  const ruleClear = T.templateRuleClear;
  fillRectFromTop(page, H, ruleClear.left, ruleClear.top, ruleClear.right, ruleClear.bottom, T.white);

  // ── Bill To body (stacked logical lines, then wrap each if needed) ────────
  const billRows = [];
  for (const segment of formatClientBillLines(client || {}, project || {})) {
    for (const ln of wrapToWidth(font, segment, bodySize, T.billMaxWidth)) {
      billRows.push(ln);
    }
  }
  let yBill = T.billTextStartY;
  for (const ln of billRows.slice(0, 6)) {
    page.drawText(ln, {
      x: T.billTextX,
      y: baselineY(H, yBill),
      size: bodySize,
      font,
      color: T.bodyText,
    });
    yBill += T.billLineHeight;
  }

  // ── Current date under INVOICE (right) ───────────────────────────────────
  const dateStr = new Date().toLocaleDateString('en-GB');
  const dateLabel = `Date: ${dateStr}`;
  const dateW = font.widthOfTextAtSize(dateLabel, dateSize);
  page.drawText(dateLabel, {
    x: T.dateRight - dateW,
    y: baselineY(H, T.dateBaselineFromTop),
    size: dateSize,
    font,
    color: T.bodyText,
  });

  // ── Table: purple header + white labels ───────────────────────────────────
  fillRectFromTop(page, H, T.tableLeft, T.headerTop, T.tableRight, T.headerBottom, T.headerFill);

  const hb = baselineY(H, 269);
  page.drawText('Sr. No', {
    x: T.colSr,
    y: hb,
    size: headerLabelSize,
    font: fontBold,
    color: T.headerText,
  });
  page.drawText('Description', {
    x: T.colDesc,
    y: hb,
    size: headerLabelSize,
    font: fontBold,
    color: T.headerText,
  });
  const amtHead = 'Amount';
  const amtHeadW = fontBold.widthOfTextAtSize(amtHead, headerLabelSize);
  page.drawText(amtHead, {
    x: T.colAmountRight - amtHeadW,
    y: hb,
    size: headerLabelSize,
    font: fontBold,
    color: T.headerText,
  });

  const totalTextY = totalRowTop + 17;

  items.forEach((it, idx) => {
    const rowBaseline = T.row0Baseline + idx * T.rowStep;
    const sr = String(idx + 1);
    const desc = (it.itemName || '').trim() || '—';
    const amt = inrCell(lineAmount(it));

    const srW = font.widthOfTextAtSize(sr, tableSize);
    page.drawText(sr, {
      x: T.colSep1 - 6 - srW,
      y: baselineY(H, rowBaseline),
      size: tableSize,
      font,
      color: T.bodyText,
    });

    let descY = rowBaseline;
    wrapToWidth(font, desc, tableSize, T.descMaxWidth)
      .slice(0, 2)
      .forEach((dl) => {
        page.drawText(dl, {
          x: T.colDesc,
          y: baselineY(H, descY),
          size: tableSize,
          font,
          color: T.bodyText,
        });
        descY += 11.5;
      });

    const amtW = font.widthOfTextAtSize(amt, tableSize);
    page.drawText(amt, {
      x: T.colAmountRight - amtW,
      y: baselineY(H, rowBaseline),
      size: tableSize,
      font,
      color: T.bodyText,
    });
  });

  // ── Gap + separate mini “Invoice Amount” table (short, right-aligned) ────
  fillRectFromTop(page, H, T.tableLeft, bodyBottom, T.tableRight, totalRowTop, T.white);

  const total = Number(invoice.total != null ? invoice.total : invoice.subTotal || 0);
  const totalStr = inrCell(total);
  fillRectFromTop(page, H, totalMiniLeft, totalRowTop, totalMiniSep, totalRowBottom, T.white);
  fillRectFromTop(page, H, totalMiniSep, totalRowTop, totalMiniRight, totalRowBottom, T.white);

  page.drawText('Invoice Amount', {
    x: totalMiniLeft + 7,
    y: baselineY(H, totalTextY),
    size: 11,
    font: fontBold,
    color: T.bodyText,
  });
  const tw = font.widthOfTextAtSize(totalStr, 12);
  page.drawText(totalStr, {
    x: totalMiniRight - tw - T.tableInnerRightMargin,
    y: baselineY(H, totalTextY),
    size: 12,
    font,
    color: T.bodyText,
  });

  drawInvoiceTableGrid(page, H, T, bodyGridRows, bodyBottom);
  drawTotalMiniTableGrid(page, H, T, totalMiniLeft, totalMiniRight, totalMiniSep, totalRowTop, totalRowBottom);

  const bytes = await pdfDoc.save();
  return new Blob([bytes], { type: 'application/pdf' });
}

export async function generateInvoicePDF(invoice, client, project = null) {
  const blob = await generateInvoicePDFBlob(invoice, client, project);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (invoice.invoiceNumber || 'Invoice') + '.pdf';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
